<template>
  <div class="date-time-picker"
       :class="{'date-time-picker-full': showTimePicker, 'date-time-picker-date': !showTimePicker}"
       :style="{'left': `${leftOffset}px`}"
       @mouseleave="lockOpen = false; $emit('closePicker')"
       @mouseenter="lockOpen = true"
  >
    <div id="datePicker"
         class="date-picker"
         tabindex="-1"
         @keydown="dateKeypress"
         @mouseenter="enterFocus"
    >
      <div class="month-picker">
        <div class="picker-btn"
             @click="alterShowMonth(-1)"
        >
          <i class="fas fa-angle-left"></i>
        </div>
        <div class="picker-value">
          {{ currentMonthLabel }} {{ showYear }}
        </div>
        <div class="picker-btn"
             @click="alterShowMonth(+1)"
        >
          <i class="fas fa-angle-right"></i>
        </div>
      </div>
      <div class="day-picker">
        <div v-for="dl in ['po','út','st','čt','pá','so','ne']"
             :key="dl"
             class="picker-label"
        >
          {{ dl }}
        </div>
        <div v-for="d in daysToSelect"
             :key="`select-${d.day}-${d.month}`"
             :class="{'today': d.fromToday === 0,
                      'holiday': d.isHoliday,
                      'weekend': d.isWeekend,
                      'highlighted': d.isHighlighted,
                      'notInMonth': !d.inCurrentMonth,
                      'inPast': d.fromToday > 0,
                      'selected': d.isSelected}"
             :title="d.holiday"
             class="day-cell"
             @click="selectDate(d)"
             @mouseenter="highlightDate(d)"
        >
          {{ d.day }}
        </div>
      </div>
      <div class="date-info">
        {{ highlightedInfo }}
      </div>
    </div>
    <div v-if="showTimePicker"
         class="hour-picker"
         tabindex="-1"
         @keydown="hourKeypress"
         @mouseenter="enterFocus"
    >
      <div v-for="h in hoursToSelect"
           :key="`hour-${h.label}`"
           :class="{'selected': h.isSelected, 'highlighted': h.isHighlighted}"
           @click="selectHour(h)"
           @mouseenter="highlightHour(h)"
      >
        {{ h.label }}
      </div>
    </div>
    <div v-if="showTimePicker"
         class="minute-picker"
         tabindex="-1"
         @keydown="minuteKeypress"
         @mouseenter="enterFocus"
    >
      <div v-for="m in minutesToSelect"
           :key="`minute-${m.label}`"
           :class="{'selected': m.isSelected, 'highlighted': m.isHighlighted}"
           @click="selectMinute(m)"
           @mouseenter="highlightMinute(m)"
      >
        {{ m.label }}
      </div>
    </div>
  </div>
</template>

<script>
import { map, join, range, includes, size, parseInt } from 'lodash'
import moment from 'CORE/utils/moment-ext'
const MINUTE_STEP = 10

// const currentTimestamp = _ => Math.floor(Date.now() / 1000) + (new Date().getTimezoneOffset() * 60)

export default {
    name: 'DateTimePicker',
    components: {
    },
    props: {
        value: {
            type: Number,
            required: false,
            default: _ => moment().unix() - (2 * 3600),
        },
        showTimePicker: {
            type: Boolean,
            required: false,
            default: _ => true,
        },
        locked: {
            type: Boolean,
            required: false,
            default: _ => false,
        },
    },
    data() {
        return {
            showMonth: 0,
            showYear: 0,
            highlightedDay: 0,
            highlightedMonth: 0,
            highlightedYear: 0,
            highlightedHour: 0,
            highlightedMinute: 0,
            typedDate: '',
            leftOffset: 0,
        }
    },
    computed: {
        valueHandler: {
            get() {
                return moment(this.value, 'X').toDate()
            },
            set(value) {
                this.$emit('input', moment(value).unix())
            },
        },
        lockOpen: {
            get() {
                return this.locked
            },
            set(value) {
                this.$emit('lockChange', value)
            },
        },
        hoursToSelect() {
            return map(range(0, 24), i => {
                return {
                    label: i,
                    hour: i,
                    isSelected: this.valueHandler.getHours() === i,
                    isHighlighted: this.highlightedHour === i,
                }
            })
        },
        minutesToSelect() {
            return map(range(0, 60, MINUTE_STEP), i => {
                return {
                    label: i,
                    minute: i,
                    isSelected: this.valueHandler.getMinutes() === i,
                    isHighlighted: this.highlightedMinute === i,
                }
            })
        },
        daysToSelect() {
            const startDay = moment({ year: this.showYear, month: this.showMonth - 1 }).startOf('month').startOf('week')
            const endDay = moment({ year: this.showYear, month: this.showMonth - 1 }).endOf('month').endOf('week')
            return map(Array.from(moment().range(startDay, endDay).by('days')), d => {
                return {
                    day: d.get('date'),
                    month: d.get('month'),
                    year: d.get('year'),
                    isHoliday: d.isHoliday(),
                    holiday: d.getHoliday(),
                    isWeekend: d.isWeekend(),
                    inCurrentMonth: d.get('month') === this.showMonth - 1,
                    isSelected: d.get('date') === this.valueHandler.getDate() && d.get('month') === this.valueHandler.getMonth(),
                    isHighlighted: d.get('date') === this.highlightedDay && d.get('month') === this.highlightedMonth,
                    fromToday: Math.ceil(moment().startOf('day').diff(d, 'days', true)),
                }
            })
        },
        currentMonthLabel() {
            return moment().month(this.showMonth - 1).format('MMMM')
        },
        highlightedInfo() {
            const m = moment({ year: this.highlightedYear, month: this.highlightedMonth, day: this.highlightedDay })
            if (m.isHoliday()) {
                return join(m.getHoliday(), ', ')
            }
            if (m.isWeekend()) {
                return 'Víkend'
            }
            return ''
        },
    },
    mounted() {
        if (!(this.valueHandler instanceof Date)) {
            this.valueHandler = new Date()
        }
        this.showMonth = this.valueHandler.getMonth() + 1
        this.showYear = this.valueHandler.getFullYear()

        this.highlightedDay = this.valueHandler.getDate()
        this.highlightedMonth = this.valueHandler.getMonth() + 1
        this.highlightedYear = this.valueHandler.getFullYear()

        this.highlightedHour = this.valueHandler.getHours()
        this.highlightedMinute = this.valueHandler.getMinutes()
        setTimeout(_ => {
            const windowRight = window.innerWidth
            const { x, width } = this.$el.getBoundingClientRect()
            const offset = windowRight - (x + width)
            if (offset < 0) {
                this.leftOffset = (offset - 5)
            } else {
                this.leftOffset = 0
            }
        }, 100)
    },
    methods: {
        enterFocus(e) {
            e.target.focus()
        },
        dateKeypress(e) {
            switch (e.code) {
                case 'ArrowRight':
                    this.alterHighlight(+1)
                    break
                case 'ArrowLeft':
                    this.alterHighlight(-1)
                    break
                case 'ArrowUp':
                    this.alterHighlight(-7)
                    break
                case 'ArrowDown':
                    this.alterHighlight(+7)
                    break
                case 'Enter':
                    this.selectDateFromHighlight()
                    // this.selectHourFromHighlight()
                    // this.selectMinuteFromHighlight()
                    break
            }
            if (includes(['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'], e.key)) {
                this.$set(this, 'typedDate', this.typedDate + e.key)
            }
            if (e.code === 'Escape') {
                this.$set(this, 'typedDate', '')
                this.highlightedDay = this.valueHandler.getDate()
                this.highlightedMonth = this.valueHandler.getMonth()
                this.highlightedYear = this.valueHandler.getFullYear()
            }
            const dayStr = this.typedDate.substring(0, 2)
            if (size(dayStr) === 2) {
                // console.info(`dayStr ${dayStr}`)
                this.highlightedDay = parseInt(dayStr)
            }
            const monthStr = this.typedDate.substring(2, 4)
            if (size(monthStr) === 2) {
                // console.info(`monthStr ${monthStr}`)
                this.highlightedMonth = parseInt(monthStr) - 1
                this.showMonth = parseInt(monthStr) - 1
            }
            const yearStr = this.typedDate.substring(4, 6)
            if (size(yearStr) === 2) {
                // console.info(`yearStr ${this.typedDate} ${parseInt(`20${yearStr}`)}`)
                this.highlightedYear = parseInt(`20${yearStr}`)
                this.showYear = parseInt(`20${yearStr}`)
            }
            const hourStr = this.typedDate.substring(6, 8)
            if (size(hourStr) === 2) {
                // console.info(`hourStr ${hourStr}`)
                this.highlightedHour = parseInt(hourStr)
            }
            const minuteStr = this.typedDate.substring(8, 10)
            if (size(minuteStr) === 2) {
                // console.info(`minuteStr ${minuteStr}`)
                this.highlightedMinute = parseInt(minuteStr)
            }
        },
        hourKeypress(e) {
            switch (e.code) {
                case 'ArrowUp':
                    this.alterHourHighlight(-1)
                    break
                case 'ArrowDown':
                    this.alterHourHighlight(+1)
                    break
                case 'Enter':
                    this.selectHourFromHighlight()
                    break
            }
        },
        minuteKeypress(e) {
            switch (e.code) {
                case 'ArrowUp':
                    this.alterMinuteHighlight(-10)
                    break
                case 'ArrowDown':
                    this.alterMinuteHighlight(+10)
                    break
                case 'Enter':
                    this.selectMinuteFromHighlight()
                    break
            }
        },
        alterHighlight(diff) {
            const nd = moment({ year: this.highlightedYear, month: this.highlightedMonth, day: this.highlightedDay }).add(diff, 'days')
            this.highlightedDay = nd.date()
            this.highlightedMonth = nd.month()
            this.highlightedYear = nd.year()
            this.showMonth = nd.month() + 1
            this.showYear = nd.year()
        },
        alterHourHighlight(diff) {
            if (this.highlightedHour + diff < 0) {
                this.highlightedHour = 0
            } else if (this.highlightedHour + diff > 23) {
                this.highlightedHour = 23
            } else {
                this.highlightedHour = this.highlightedHour + diff
            }
        },
        alterMinuteHighlight(diff) {
            if (this.highlightedMinute + diff < 0) {
                this.highlightedMinute = 0
            } else if (this.highlightedMinute + diff > 59) {
                this.highlightedMinute = 50
            } else {
                this.highlightedMinute = this.highlightedMinute + diff
            }
        },
        alterShowMonth(diff) {
            const newMonth = this.showMonth + diff
            if (newMonth < 1) {
                this.showMonth = 12
                this.showYear = this.showYear - 1
            } else if (newMonth > 12) {
                this.showMonth = 1
                this.showYear = this.showYear + 1
            } else {
                this.showMonth = newMonth
            }
        },
        selectDate(d) {
            this.showMonth = d.month + 1
            this.showYear = d.year
            this.valueHandler = moment({
                year: d.year,
                month: d.month,
                day: d.day,
                hour: this.valueHandler.getHours(),
                minute: this.valueHandler.getMinutes(),
            }).toDate()
        },
        selectHour(h) {
            this.valueHandler = moment({
                year: this.valueHandler.getFullYear(),
                month: this.valueHandler.getMonth(),
                day: this.valueHandler.getDate(),
                hour: h.hour,
                minute: this.valueHandler.getMinutes(),
            }).toDate()
        },
        selectMinute(m) {
            this.valueHandler = moment({
                year: this.valueHandler.getFullYear(),
                month: this.valueHandler.getMonth(),
                day: this.valueHandler.getDate(),
                hour: this.valueHandler.getHours(),
                minute: m.minute,
            }).toDate()
        },
        selectDateFromHighlight() {
            this.valueHandler = moment({
                year: this.highlightedYear,
                month: this.highlightedMonth,
                day: this.highlightedDay,
                hour: this.valueHandler.getHours(),
                minute: this.valueHandler.getMinutes(),
            }).toDate()
        },
        selectHourFromHighlight() {
            this.valueHandler = moment({
                year: this.valueHandler.getFullYear(),
                month: this.valueHandler.getMonth(),
                day: this.valueHandler.getDate(),
                hour: this.highlightedHour,
                minute: this.valueHandler.getMinutes(),
            }).toDate()
        },
        selectMinuteFromHighlight() {
            this.valueHandler = moment({
                year: this.valueHandler.getFullYear(),
                month: this.valueHandler.getMonth(),
                day: this.valueHandler.getDate(),
                hour: this.value.getHours(),
                minute: this.highlightedMinute,
            }).toDate()
        },
        highlightDate(d) {
            this.highlightedDay = d.day
            this.highlightedMonth = d.month
            this.highlightedYear = d.year
        },
        highlightHour(h) {
            this.highlightedHour = h.hour
        },
        highlightMinute(m) {
            this.highlightedMinute = m.minute
        },
    },
}
</script>

<style scoped>
.date-time-picker {
    display: grid;
    grid-column-gap: 20px;
    border: 1px solid var(--color-primary);
    width: max-content;
    padding: 5px;
    border-radius: 5px;
    box-shadow: 0px 0px 6px 0px rgba(0,0,0,0.3);
    position: absolute;
    overflow: hidden;
    height: 270px;
    top: 45px;
    left: 0;
    background-color: white;
    z-index: 999;
}
.date-time-picker-full {
    grid-template-columns: 250px 120px 60px;
}
.date-time-picker-date {
    grid-template-columns: 250px;
}
.date-time-picker > div:focus {
    outline: none !important;
}
.day-picker {
    width: min-content;
    padding: 5px;
    display: grid;
    grid-template-columns: 30px 30px 30px 30px 30px 30px 30px;
    grid-gap: 5px;
    overflow: hidden;
}
.day-picker .day-cell {
    width: 30px;
    height: 30px;
    text-align: center;
    line-height: 20px;
    border: 1px solid lightgray;
    cursor: pointer;
    min-height: 30px;
}
.day-picker > .today {
    font-weight: bold;
    color: red;
    font-size: 120%;
}
.day-picker > .weekend {
    background-color: LemonChiffon;
}
.day-picker > .holiday {
    background-color: AntiqueWhite;
}
.day-picker > .selected {
    background-color: Orange;
}
.day-picker > .inPast {
    border-style: dashed;
    border-radius: 50%;
}
.day-picker > .notInMonth {
    color: lightgrey;
    border-color: transparent;
}
.date-time-picker > div:focus .day-picker > .highlighted {
    border: 1px solid red;
}
.year-picker, .month-picker {
    display: flex;
    margin: 5px;
}
.picker-value {
    flex-grow: 1;
    line-height: 20px;
    text-align: center;
}
.picker-btn {
    width: 30px;
    height: 20px;
    border: 1px solid gray;
    background-color: lightslategray;
    line-height: 20px;
    text-align: center;
    display: block;
    cursor: pointer;
}
.date-info {
    font-size: 80%;
    color: dimgray
}
.hour-picker, .minute-picker {
    height: 240px;
    overflow: hidden;
}
.hour-picker {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
}
.hour-picker > div, .minute-picker > div {
    line-height: 20px;
    text-align: center;
    height: 20px;
    border: 1px solid silver;
    margin: 2px;
    cursor: pointer;
}
.minute-picker > div {
    width: 56px;
}
.hour-picker > div.selected, .minute-picker > div.selected {
    background-color: Orange;
}
.hour-picker > div.highlighted, .minute-picker > div.highlighted {
    border: 1px solid red;
}
.picker-label {
    text-align: center;
    font-size: 90%;
    font-weight: bold;
}
</style>
